import React, { useContext, useEffect, useState } from "react";
import {
  Table,
  Space,
  Button,
  Input,
  Drawer,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { PageHeader } from "../../../../components/page-header/page-header";
import useFetch from "../../../../hooks/useFetch";
import {
  AddEditFaultCategory,
  GetFaultCategoryList,
} from "../../../../utils/api.constant";
import { ePrivileges, eREGIONS, eDATE_FORMAT, eResultCode } from "../../../../utils/enum";
import useToast from "../../../../hooks/useToast";
import { ToastType, ToastOpen } from "../../../../state/toast/slice";
import CustomDrawer from "../../../../components/drawer";
import FaultCategoryForm from "./form";
import { UserContext } from "../../../../context";
import ActionCard from "../../../../components/actionCard/actionCard";
import { TContext, TFilterModel, TPrivilege } from "../../../../types/config";
import GenericPopover from "../../../../components/pop-over";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  EditFilled,
  FilterOutlined,
  IdcardFilled,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../../components/configurable-columns";
import ExportToCSV from "../../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../../utils/date";
import { defaultFilterParams } from "../../../../utils/constants";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../../utils/column-management";
import { TFaultCategory } from "../../../../types";
import CustomModal from "../../../../components/modal";
import Utils from "../../../../utils";
import { useColumnSearch } from "../../../../components/table/column-search";

const FAULT_LIST_COLUMN_KEY = "faultCategoryList";

export default function FaultCategoryList() {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TFaultCategory[]>([]);
  const [faultCategory, setFaultCategory] = useState<TFaultCategory | null>();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const context: TContext = useContext(UserContext);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const { getDateRangeFilterProps } = useColumnSearch<TFaultCategory>();

  const handleDelete = (faultCategory: TFaultCategory) => {
    setFaultCategory(faultCategory);
    setShowDeleteModal(true);
  };

  const initialColumns: any[] = [
    {
      title: "Name",
      dataIndex: "problemName",
      key: "problemName",
      // width: 450,
      filterSearch: true,
      onFilter: (value: string | number | boolean, record: TFaultCategory) =>
        record.problemName
          ?.toLowerCase()
          ?.includes((value as string).toLowerCase()),
      sorter: (a: TFaultCategory, b: TFaultCategory) =>
        a.problemName?.localeCompare?.(b.problemName),
      filteredValue: activeFilters.problemName || null,
    },
    {
      title: "Added On",
      dataIndex: "createdOn",
      key: "createdOn",
      // width: 150,
      sorter: (a: TFaultCategory, b: TFaultCategory) =>
        new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
      ...getDateRangeFilterProps("createdOn"),
      render: (createdOn: string) => {
        if (createdOn) {
          return DateUtils.format(createdOn, context?.user?.dateFormat, true);
        }
        return "-"; // Or any placeholder for empty values
      },
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      fixed: "right",
      width: 80,
      render: (record: TFaultCategory) => (
        <GenericPopover
          placement="left"
          content={
            <div className="standardActionBox">
              {Utils.isUserHasAccess(
                context.user?.privilegeList as TPrivilege[],
                ePrivileges.ADDEDITFAULTCATEGORY,
              ) ? (
                <CustomDrawer
                  trigger={
                    <ActionCard text="Edit">
                      <EditFilled />
                    </ActionCard>
                  }
                  component={FaultCategoryForm}
                  componentProps={{
                    id: record.id,
                    onRefreshList: fetchData,
                  }}
                  title="Fault Category Form"
                  size="default"
                />
              ) : null}

              {Utils.isUserHasAccess(
                context.user?.privilegeList as TPrivilege[],
                ePrivileges.DELETEFAULTCATEGORY,
              ) ? (
                <ActionCard text="Delete" onClick={() => handleDelete(record)}>
                  <DeleteOutlined />
                </ActionCard>
              ) : null}
            </div>
          }
          icon={
            <MoreOutlined
              style={{
                fontSize: "20px",
                cursor: "pointer",
                display:
                  Utils.isUserHasAccess(
                    context.user?.privilegeList as TPrivilege[],
                    ePrivileges.ADDEDITFAULTCATEGORY,
                  ) ||
                  Utils.isUserHasAccess(
                    context.user?.privilegeList as TPrivilege[],
                    ePrivileges.DELETEFAULTCATEGORY,
                  )
                    ? "visible"
                    : "none",
              }}
            />
          }
        />
      ),
    },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    (async () => {
      const savedPreferences: any[] = await loadColumnPreferences(
        FAULT_LIST_COLUMN_KEY,
      );

      if (savedPreferences && savedPreferences.length > 0) {
        const savedPrefsMap: Map<string, any> = new Map(
          savedPreferences.map((pref: any) => [pref.key, pref]),
        );
        const mergedColumns = initialColumns.map((col) => {
          const savedPref = savedPrefsMap.get(col.key) as any;
          return {
            ...col,
            hidden: savedPref?.hidden ?? false,
          };
        });
        initialColumns.forEach((col) => {
          if (!savedPrefsMap.has(col.key)) {
            if (!mergedColumns.find((c) => c.key === col.key)) {
              mergedColumns.push({ ...col, hidden: false });
            }
          }
        });

        setColumns(mergedColumns);
        setDraftColumns(mergedColumns);
      } else {
        setColumns(initialColumns);
        setDraftColumns(initialColumns);
      }
    })();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetFaultCategoryList, requestPayload);
      const { dataResponse, data, filterModel } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map(
          (item: TFaultCategory, index: number) => ({
            ...item,
            key: (item.id ?? "").toString(),
            srNo: index + 1,
          }),
        );
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        updateColumnFilters(formattedData, activeFilters, searchText);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Failed to fetch fault categroy data",
      });
      setIsLoading(false);
    }
  };

  const updateColumnFilters = (
    dataset: TFaultCategory[],
    currentFilters: Record<string, any>,
    currentSearchText: string,
  ) => {
    const getFilters = (key: keyof TFaultCategory) => {
      const validRowsForThisColumn = dataset.filter((item) => {
        // Global search match
        const searchMatch =
          !currentSearchText ||
          Object.values(item).some(
            (val) =>
              val &&
              val
                .toString()
                .toLowerCase()
                .includes(currentSearchText.toLowerCase()),
          );

        // Check each active filter
        const filterMatches = Object.entries(currentFilters).every(
          ([filterKey, filterValue]) => {
            if (
              !filterValue ||
              (Array.isArray(filterValue) && filterValue.length === 0) ||
              filterKey === key
            )
              return true;

            // Date Range Match (nested dayjs arrays)
            if (
              filterValue.length > 0 &&
              Array.isArray(filterValue[0]) &&
              filterValue[0].length === 2
            ) {
              const startDayjs = filterValue[0][0];
              const endDayjs = filterValue[0][1];
              if (startDayjs && endDayjs && "valueOf" in startDayjs) {
                const start = new Date(startDayjs.valueOf()).setHours(
                  0,
                  0,
                  0,
                  0,
                );
                const end = new Date(endDayjs.valueOf()).setHours(
                  23,
                  59,
                  59,
                  999,
                );
                const itemDateVal = item[filterKey as keyof TFaultCategory];
                if (!itemDateVal) return false;
                const rowDate = new Date(itemDateVal as string).getTime();
                return rowDate >= start && rowDate <= end;
              }
            }

            const column = initialColumns.find(
              (col) => col.key === filterKey || col.dataIndex === filterKey,
            );

            if (column && column.onFilter) {
              return Array.isArray(filterValue)
                ? filterValue.some((val: any) => column.onFilter!(val, item))
                : column.onFilter!(filterValue, item);
            }

            const itemValue = item[filterKey as keyof TFaultCategory];
            return Array.isArray(filterValue)
              ? filterValue.includes(itemValue)
              : itemValue === filterValue;
          },
        );

        return searchMatch && filterMatches;
      });

      const uniqueValues = Array.from(
        new Set(
          validRowsForThisColumn
            ?.map((item) => item?.[key])
            ?.filter(
              (val) =>
                val !== null && val !== undefined && val !== "" && val !== "-",
            ) as string[],
        ),
      );

      return uniqueValues.map((value) => ({ text: value, value }));
    };

    const nameFilters = getFilters("problemName");

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.dataIndex === "problemName") {
          return { ...col, filters: nameFilters };
        }
        return col;
      }),
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    updateColumnFilters(data, activeFilters, searchText);
  }, [activeFilters, data, searchText]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const searchMatch =
      !searchText ||
      Object.values(item).some(
        (val) =>
          val &&
          val.toString().toLowerCase().includes(searchText.toLowerCase()),
      );

    const filterMatches = Object.entries(activeFilters).every(
      ([filterKey, filterValue]) => {
        if (
          !filterValue ||
          (Array.isArray(filterValue) && filterValue.length === 0)
        )
          return true;

        // Date Range Match (nested dayjs arrays)
        if (
          filterValue.length > 0 &&
          Array.isArray(filterValue[0]) &&
          filterValue[0].length === 2
        ) {
          const startDayjs = filterValue[0][0];
          const endDayjs = filterValue[0][1];
          if (startDayjs && endDayjs && "valueOf" in startDayjs) {
            const start = new Date(startDayjs.valueOf()).setHours(0, 0, 0, 0);
            const end = new Date(endDayjs.valueOf()).setHours(23, 59, 59, 999);
            const itemDateVal = item[filterKey as keyof TFaultCategory];
            if (!itemDateVal) return false;
            const rowDate = new Date(itemDateVal as string).getTime();
            return rowDate >= start && rowDate <= end;
          }
        }

        const column = initialColumns.find(
          (col) => col.key === filterKey || col.dataIndex === filterKey,
        );

        if (column && column.onFilter) {
          return Array.isArray(filterValue)
            ? filterValue.some((val: any) => column.onFilter!(val, item))
            : column.onFilter!(filterValue, item);
        }

        const itemValue = item[filterKey as keyof TFaultCategory];
        return Array.isArray(filterValue)
          ? filterValue.includes(itemValue)
          : itemValue === filterValue;
      },
    );

    return searchMatch && filterMatches;
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns);
    clearColumnPreferences(FAULT_LIST_COLUMN_KEY);
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns);
    saveColumnPreferences(FAULT_LIST_COLUMN_KEY, draftColumns);
    setDrawerVisible(false);
  };

  const visibleColumns = columns?.filter((col) => !col?.hidden);

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
  };

  const deleteProduct = async (faultCategory: TFaultCategory) => {
    try {
      const payload = {
        data: {
          ...faultCategory,
          isDeleted: 1,
        },
      };
      const response = await post(AddEditFaultCategory, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleDeleteOk = async () => {
    if (faultCategory) {
      await deleteProduct(faultCategory);
    }
    setFaultCategory(null);
    setShowDeleteModal(false);
  };

  const allKeys = Array.from(
    new Set(data.flatMap((item) => Object.keys(item))),
  );

  const formatExportRow = (item: any) => {
    const mapped = {
      ...item,
      createdOn: DateUtils.formatForExport(
        item?.createdOn,
        true,
        context?.user?.dateFormat as eDATE_FORMAT,
      ),
      updatedOn: DateUtils.formatForExport(
        item?.updatedOn,
        true,
        context?.user?.dateFormat as eDATE_FORMAT,
      ),
    };

    const completeRow: Record<string, any> = {};
    allKeys.forEach((key) => {
      const val = (mapped as any)[key];
      completeRow[key] =
        val === null ||
        val === undefined ||
        val === "" ||
        (Array.isArray(val) && val.length === 0)
          ? "-"
          : val;
    });

    return completeRow;
  };

  const exportData = data.map(formatExportRow);
  const exportFilteredData = filteredData.map(formatExportRow);

  const handleDeleteCancel = () => {
    setFaultCategory(null);
    setShowDeleteModal(false);
  };

  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardFilled />}
        title={`${filterParams.totalRows} Total Fault Categories`}
        size="large"
      >
        <Space className="headerItems" wrap>
          {/* {Utils.isUserHasAccess(
              context.user?.privilegeList as TPrivilege[],
              ePrivileges.ADDCUSTOMERS
            ) ? ( */}
          <CustomDrawer
            trigger={
              <Button disabled={isLoading} type="primary">
                Add
              </Button>
            }
            component={FaultCategoryForm}
            componentProps={{
              id: 0,
              onRefreshList: fetchData,
            }}
            title="Fault Category Form"
            size="default"
          />
          {/* ) : null} */}
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />

          <ExportToCSV
            allColumns={initialColumns}
            data={exportData}
            filteredData={exportFilteredData}
            visibleColumns={visibleColumns}
            moduleName="Fault Category"
          />

          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined
              onClick={() => {
                setDraftColumns(columns);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          // sticky
          scroll={{
            x: "max-content",
          }}
        />
      </div>

      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>

      <CustomModal
        title="Confirm Delete"
        isOpen={showDeleteModal}
        onOk={handleDeleteOk}
        onCancel={() => handleDeleteCancel()}
        okText="Yes"
        cancelText="No"
        cusHeader={true}
      >
        <p>
          Are you about to delete <b>{faultCategory?.problemName}</b>?
        </p>
      </CustomModal>
    </div>
  );
}
